import { AppMenu, Flex, Text } from '@ghq-abi/design-system';
import Image from 'next/image';
import { useCallback, useRef } from 'react';
import cheersMenuLogo from '~/../public/img/cheers_menu_logo.png';
import { useNav } from '~/app/contexts/NavContext';
import { RouteObject } from '~/app/types';
import { useUser } from '~/shared/auth/useUser';
import { BackArrowIcon } from '~/shared/components/icons';
import { useEventsAnalytics } from '~/shared/hooks/useEvaluationAnalytics';
import { useMenuListScroll } from '../../hooks/useMenuListScroll';
import { MenuSkeleton } from '../MenuSkeleton';

const openExternal = (url?: string) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function DesktopMenu() {
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuHeaderItem,
    menuItems,
    footerItems,
    isMenuLoading,
    toggleAppMenu,
    checkIfIsActive,
    toggleSubMenu,
  } = useNav();

  const { elementRef, isScrollable } = useMenuListScroll();

  const user = useUser();
  const eventsAnalytics = useEventsAnalytics();

  const analyticsExecuted = useRef(new Set<string>());

  const handleTriggerAnalyticsEvent = useCallback(
    (itemKey: string) => {
      if (analyticsExecuted.current.has(itemKey)) {
        return;
      }

      analyticsExecuted.current.add(itemKey);

      if (itemKey === 'Global Policies') {
        eventsAnalytics.fireClickGlobalPoliciesAnalyticsEvent(user);
      }
      if (itemKey === 'Global Trainings') {
        eventsAnalytics.fireClickGlobalTrainingsAnalyticsEvent(user);
      }
      if (itemKey === 'Compliance Helpline') {
        eventsAnalytics.fireClickHelpLineAnalyticsEvent(user);
      }

      setTimeout(() => {
        analyticsExecuted.current.delete(itemKey);
      }, 1000);
    },
    [eventsAnalytics, user]
  );

  const handleMenuItemClick = (item: RouteObject) => {
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      return;
    }
    if (item.routeComponents?.length) {
      toggleSubMenu(item);
    }
  };

  if (isMenuLoading) {
    return <MenuSkeleton />;
  }

  return (
    <AppMenu open={isAppMenuOpen}>
      <AppMenu.Trigger asChild>
        <AppMenu.Bar openMenu={toggleAppMenu} openMenuDelay={300}>
          <AppMenu.Header css={{ p: 0 }}>
            <AppMenu.MenuItem
              css={{ p: '$3 $2' }}
              leftIcon={menuHeaderItem.icon}
              isTriggerItem
            />
          </AppMenu.Header>

          <AppMenu.MenuList>
            {menuItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                leftIcon={item.icon}
                isActive={
                  item.path
                    ? checkIfIsActive(item.path, item.routeComponents)
                    : false
                }
                isTriggerItem
              />
            ))}
          </AppMenu.MenuList>

          <AppMenu.Footer>
            {footerItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                leftIcon={item.icon}
                isActive={
                  item.path
                    ? checkIfIsActive(item.path, item.routeComponents)
                    : false
                }
                isTriggerItem
              />
            ))}
          </AppMenu.Footer>
        </AppMenu.Bar>
      </AppMenu.Trigger>

      <AppMenu.Content
        closeMenuDelay={300}
        closeMenu={() => {
          toggleAppMenu();
          toggleSubMenu(null);
        }}
      >
        <AppMenu.Header css={{ p: 0 }}>
          <AppMenu.MenuItem
            title={menuHeaderItem.title}
            hasSubmenu
            css={{ p: '$3 $2' }}
            leftIcon={menuHeaderItem.icon}
            isSubMenuOpen={subMenuOpen?.key === menuHeaderItem.key}
            onClick={() => {
              if (menuHeaderItem.external) {
                openExternal(menuHeaderItem.path);
              } else if (menuHeaderItem.routeComponents?.length) {
                toggleSubMenu(menuHeaderItem);
              }
            }}
            renderSubmenu={
              <AppMenu.SubMenu
                title={menuHeaderItem.title || ''}
                alignWithHeader
                hideTrigger
                css={{ marginLeft: '220px', borderRadius: '$4' }}
                renderTitle={
                  <Flex align="center" gap="md" css={{ height: '$full' }}>
                    <AppMenu.MenuIconButton
                      onClick={() => toggleSubMenu(null)}
                      icon={<BackArrowIcon />}
                    />
                    <Text css={{ fontWeight: '$medium' }}>
                      {menuHeaderItem.title}
                    </Text>
                  </Flex>
                }
              >
                <AppMenu.MenuList>
                  {menuHeaderItem.routeComponents?.map(subitem => (
                    <AppMenu.MenuItem
                      key={subitem.key}
                      title={subitem.title}
                      route={subitem.external ? undefined : subitem.path}
                      isSubMenuItem
                      onClick={
                        subitem.external
                          ? () => openExternal(subitem.path)
                          : undefined
                      }
                    />
                  ))}
                </AppMenu.MenuList>
              </AppMenu.SubMenu>
            }
          />
        </AppMenu.Header>

        <AppMenu.MenuList
          isMainList
          ref={elementRef}
          bottomElement={
            <Image src={cheersMenuLogo} placeholder="blur" alt="Cheers Hub" />
          }
          bottomElementCss={{
            width: isScrollable
              ? 'calc($appMenuOpenWidth - $2)'
              : '$appMenuOpenWidth',
          }}
        >
          {menuItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              route={item.external ? undefined : item.path}
              title={item.title}
              leftIcon={item.icon}
              onClick={(() => {
                if (item.external) {
                  return () => {
                    handleTriggerAnalyticsEvent(item.key);
                    openExternal(item.path);
                  };
                }
                if (item.routeComponents?.length) {
                  return () => {
                    handleTriggerAnalyticsEvent(item.key);
                    toggleSubMenu(item);
                  };
                }
                return undefined;
              })()}
              hasSubmenu={!!item.routeComponents?.length}
              isSubMenuOpen={subMenuOpen?.key === item.key}
              onHover={() => toggleSubMenu(item)}
              onHoverDelay={300}
              isActive={
                item.path && !item.external
                  ? checkIfIsActive(item.path, item.routeComponents)
                  : false
              }
              renderSubmenu={
                <AppMenu.SubMenu
                  title={item.title || ''}
                  css={{ borderRadius: '$4' }}
                >
                  <AppMenu.MenuList>
                    {item.routeComponents?.map(subitem => (
                      <AppMenu.MenuItem
                        key={subitem.key}
                        title={subitem.title}
                        route={subitem.external ? undefined : subitem.path}
                        isSubMenuItem
                        onClick={
                          subitem.external
                            ? () => openExternal(subitem.path)
                            : undefined
                        }
                      />
                    ))}
                  </AppMenu.MenuList>
                </AppMenu.SubMenu>
              }
            />
          ))}
        </AppMenu.MenuList>

        <AppMenu.Footer>
          {footerItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              route={item.external ? undefined : item.path}
              title={item.title}
              hasSubmenu={!!item.routeComponents?.length}
              leftIcon={item.icon}
              onClick={(() => {
                if (item.external) {
                  return () => openExternal(item.path);
                }
                if (item.routeComponents?.length) {
                  return () => toggleSubMenu(item);
                }
                return undefined;
              })()}
              isSubMenuOpen={subMenuOpen?.key === item.key}
              onHover={() => toggleSubMenu(item)}
              onHoverDelay={300}
              isActive={
                item.path && !item.external
                  ? checkIfIsActive(item.path, item.routeComponents)
                  : false
              }
              renderSubmenu={
                <AppMenu.SubMenu
                  className="rounded-md"
                  title={item.title || ''}
                >
                  <AppMenu.MenuList>
                    {item.routeComponents?.map(subitem => (
                      <AppMenu.MenuItem
                        key={subitem.key}
                        title={subitem.title}
                        route={subitem.external ? undefined : subitem.path}
                        isSubMenuItem
                        onClick={
                          subitem.external
                            ? () => openExternal(subitem.path)
                            : undefined
                        }
                      />
                    ))}
                  </AppMenu.MenuList>
                </AppMenu.SubMenu>
              }
            />
          ))}
        </AppMenu.Footer>
      </AppMenu.Content>
    </AppMenu>
  );
}
